import * as R from 'ramda';
import React, { useEffect, useMemo } from 'react';

import { Modals, PlayerErrorMessages } from '@/constants';
import { log } from '@/services';
import {
  playerActions,
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPlayerBuffering,
  selectIsPlaying,
  selectIsTrackLoaded,
  selectPersistedPlayerSpeedPreference,
  selectPersistedPlayerVolumePreference,
  selectPlayerSeekToPosition,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useFetchChapterURLsQuery,
} from '@/store';

interface AudioVideoPlayerProps {
  isVideo?: boolean;
  trackPlayerRef: React.RefObject<HTMLVideoElement | null>;
}

const LOCK_KEY = 'player.transition';
const eventLock = {
  lastEvent: null as Date | null,
  count: 0,
};

export const AudioVideoPlayer = ({
  isVideo,
  trackPlayerRef,
}: AudioVideoPlayerProps) => {
  const dispatch = useAppDispatch();

  const isPlayerPlaying = useAppSelector(selectIsPlaying);
  const volume = useAppSelector(selectPersistedPlayerVolumePreference);
  const playerSpeed = useAppSelector(selectPersistedPlayerSpeedPreference);
  const isTrackLoaded = useAppSelector(selectIsTrackLoaded);
  const seekToPosition = useAppSelector(selectPlayerSeekToPosition);
  const isBuffering = useAppSelector(selectIsPlayerBuffering);

  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const activeChapterId = useAppSelector(selectActiveChapterId);

  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );

  const { data } = useFetchChapterURLsQuery(
    {
      episodeId: activeEpisodeId!,
      compliance,
    },
    { skip: !activeEpisodeId },
  );

  const playerStreamUrl = useMemo(
    () => data?.find(c => c.chapterId === activeChapterId)?.url,
    [data, activeChapterId],
  );

  useEffect(() => {
    if (!isTrackLoaded || !seekToPosition || !trackPlayerRef.current) return;

    try {
      trackPlayerRef.current.currentTime = seekToPosition;
    } catch (error) {
      dispatch(playerActions.clearSeekToPosition());
      log(error);
    }
  }, [dispatch, isTrackLoaded, seekToPosition, trackPlayerRef]);

  useEffect(() => {
    if (!isTrackLoaded) return;

    navigator.locks.request(LOCK_KEY, async () => {
      if (
        !!eventLock.lastEvent &&
        Date.now() - eventLock.lastEvent.getTime() < 100
      ) {
        if (eventLock.count > 3) {
          throw new Error('AudioVideoPlayer lock request too frequent');
        }
        eventLock.count += 1;
        log(`throttling play/pause toggle
          previous timestamp: ${eventLock.lastEvent?.toISOString()},
          count: ${eventLock.count}`);
      } else {
        eventLock.count = 0;
      }
      eventLock.lastEvent = new Date();
      if (
        !trackPlayerRef.current ||
        trackPlayerRef.current.paused === !isPlayerPlaying
      ) {
        return;
      }

      if (isPlayerPlaying) {
        await trackPlayerRef.current?.play();
      } else {
        trackPlayerRef.current?.pause();
      }
    });
  }, [dispatch, isPlayerPlaying, isTrackLoaded, trackPlayerRef]);

  useEffect(() => {
    if (!trackPlayerRef.current) return;

    trackPlayerRef.current.volume = volume / 100;
  }, [volume, trackPlayerRef]);

  useEffect(() => {
    if (!trackPlayerRef.current) return;

    trackPlayerRef.current.playbackRate = playerSpeed;
  }, [playerSpeed, playerStreamUrl, trackPlayerRef]); // Can't set playback rate until the playerStreamUrl is available

  const onReady = () => {
    dispatch(playerActions.setTrackLoaded(true));
  };

  const onBuffer = () => {
    dispatch(playerActions.setBuffering(true));
  };

  const onBufferEnd = () => {
    dispatch(playerActions.setBuffering(false));
  };

  const onSeek = () => {
    const position = trackPlayerRef.current?.currentTime;

    if (R.isNil(position)) return;
    dispatch(
      playerThunks.seekToPosition.fulfilled(undefined, '', {
        position,
      }),
    );
  };

  const onProgress = () => {
    if (isBuffering) return;
    try {
      const playedSeconds = trackPlayerRef.current?.currentTime;
      const buffered = trackPlayerRef.current?.buffered;

      if (!isTrackLoaded || R.isNil(playedSeconds)) return;

      dispatch(
        playerThunks.updatePlayerProgress({
          progress: {
            position: playedSeconds,
            buffered,
          },
        }),
      );
    } catch (error) {
      log(error);
    }
  };

  const onEnded = () => {
    dispatch(playerThunks.chapterEnded());
  };

  const onError = () => {
    const error = trackPlayerRef.current?.error;
    if (!error) return;

    if (
      PlayerErrorMessages.BROWSER_AUTOPLAY_PROHIBITED.some(errorMessage =>
        error.message?.includes(errorMessage),
      )
    ) {
      dispatch(playerThunks.pausePlayer());
      dispatch(
        uiActions.setActiveModal({ name: Modals.BROWSER_AUTOPLAY_PROHIBITED }),
      );
      return;
    }

    log(
      new Error('AudioVideoPlayer library error handler', {
        cause: new Error(error?.message),
      }),
      {
        playerStreamUrl,
      },
      error,
    );
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const Tag = isVideo ? 'video' : 'audio';

  return (
    <Tag
      ref={trackPlayerRef}
      src={playerStreamUrl}
      onEnded={onEnded}
      onTimeUpdate={onProgress}
      onSeeked={onSeek}
      onError={onError}
      onCanPlay={onReady}
      onWaiting={onBuffer}
      onPlaying={onBufferEnd}
      playsInline // required for iOS Safari
      controls={false}
      width="100%"
      height="100%"
      disablePictureInPicture
      onContextMenu={onContextMenu}
    />
  );
};
